import { LogBody, LogLevel, LogMessage } from '../../../shared/logging/types'
import { createDebugLogger } from 'shared/debug'
import { _map, _noop } from './script-utils'

const debug = createDebugLogger('logmanager')

export type LogTransport = (
	serializedLogs: string,
	onSuccess: () => void,
	onError: () => void,
) => void

function toLogBody(message: string | LogBody | Error): LogBody {
	if (message instanceof Error) {
		return { message: message.message, stack: message.stack }
	}
	return typeof message === 'string' ? { message } : message
}

function fetchTransport(serializedLogs: string, onSuccess: () => void, onError: () => void): void {
	fetch('/log', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: serializedLogs,
	}).then(() => onSuccess()).catch(() => onError())
}

export const LogManager = new class {
	private logQueue: QueuedLogMessage[] = []
	private transport: LogTransport = fetchTransport
	private playerIdResolver: () => number = () => -1

	constructor() {
		// Batch and send logs every 5 seconds.
		setInterval(() => this.sendLogs().catch(_noop), 5000)
	}

	setTransport(transport: LogTransport): void {
		this.transport = transport
	}

	setPlayerIdResolver(resolver: () => number): void {
		this.playerIdResolver = resolver
	}

	log(body: LogMessage): void {
		this.scheduleLog(this.constructLogMessage(body.level, body.data))
	}

	error(message: string | LogBody | Error): void {
		this.scheduleLog(this.constructLogMessage(LogLevel.ERROR, toLogBody(message)))
	}

	warn(message: string | LogBody | Error): void {
		this.scheduleLog(this.constructLogMessage(LogLevel.WARN, toLogBody(message)))
	}

	info(message: string | LogBody): void {
		this.scheduleLog(this.constructLogMessage(LogLevel.INFO, toLogBody(message)))
	}

	// To help identify intent of logging
	metric(data: LogBody): void {
		this.info(data)
	}

	deadCode(location: string): void {
		this.info({ message: 'Dead code test', location })
	}

	flush(): Promise<void> {
		return this.sendLogs()
	}

	private constructLogMessage(level: LogLevel, body: LogBody): LogMessage {
		return {
			level,
			time: new Date().toISOString(),
			data: {
				...body,
				playerDetails: {
					playerId: this.playerIdResolver(),
					screenname: '<redacted>',
					userAgent: navigator.userAgent,
					uniqueSessionId, // Global variable from index.html
				},
				clientVersion: buildVersion,
			},
		}
	}

	private scheduleLog(message: LogMessage, retries: number = 3) {
		this.logQueue.push({ message, retries })
	}

	private sendLogs(): Promise<void> {
		if (this.logQueue.length === 0) {
			return Promise.resolve()
		}

		const logs = this.logQueue
		this.logQueue = []

		return new Promise((resolve, reject) => {
			try {
				const postData = JSON.stringify(_map(logs, 'message'))
				this.transport(
					postData,
					() => resolve(),
					() => {
						logs.forEach(log => {
							if (log.retries === 0) {
								debug('Failed to submit client log: %o', log.message)
							} else {
								this.scheduleLog(log.message, log.retries - 1)
							}
						})
						reject()
					},
				)
			} catch (e) {
				// This error should only occur when the log body cannot be serialized (circular reference)
				const message = 'Failed to prepare log batch, reason: ' + e
				this.error({ message })
				reject()
			}
		})
	}
}

interface QueuedLogMessage {
    retries: number;
    message: LogMessage;
}

export enum MetricGroup {
    InClientNotifications = 'InClientNotifications',
}
